import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusHiddenOfferLabel from './LuxPlusHiddenOfferLabel'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { getThemeClassName } from 'lib/theme/themeUtils'
import useHiddenOfferClickHandler from './useHiddenOfferClickHandler'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'
import Colour from 'components/utils/Colour'

interface Props {
  offer: App.Offer | App.OfferSummary
  urgencyLabels?: Array<App.OfferUrgencyLabel>
}

function LuxPlusHiddenOfferDetails(props: Props) {
  const {
    offer,
    urgencyLabels,
  } = props
  const intl = useIntl()
  const onHandleHiddenOffer = useHiddenOfferClickHandler(offer)

  const pkg = offer.lowestPricePackage!
  const {
    valueOffPercent,
  } = useOfferPackagePrice(pkg, offer, { showMemberPrice: true })

  /**
   * If the locationHeading and locationSubheading are the same, only display the locationSubheading
   * e.g. Singapore, Singapore -> Singapore
  */
  const formattedLocationHeading = useMemo(() => {
    if (offer.locationHeading === offer.locationSubheading) return offer.locationSubheading
    return intl.formatMessage(
      {
        id: 'JPIdHoddJ6',
        defaultMessage: '{locationHeading}, {locationSubheading}',
        description: 'LuxPlus hidden offer location heading and subheading separated by comma',
      },
      { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
    )
  }, [offer.locationHeading, offer.locationSubheading, intl])

  return <Group gap={16} direction="vertical" verticalAlign="space-between">
    <VerticalSpacer gap={8}>
      {!earlyAccessRestrictedToMembers(offer) && <LuxPlusHiddenOfferLabel className={getThemeClassName({ mobile: 'inverse' })} />}
      {earlyAccessRestrictedToMembers(offer) &&
        <OfferLabels
          offer={offer}
          urgencyLabels={urgencyLabels}
        />}
      <Heading variant="heading5">
        <FormattedMessage
          id="13zt5tQpDW"
          defaultMessage="<highlighted>Members save up to {valueOffPercent}%</highlighted> on this hidden offer in {location}"
          description="Heading showing member savings percentage on hidden hotel offer"
          values={{
            valueOffPercent,
            location: formattedLocationHeading,
            highlighted: (chunks) => <Colour value="highlight-secondary">{chunks}</Colour>,
          }}
        />
      </Heading>
    </VerticalSpacer>

    <div>
      <ProductPaletteProvider palette="lux-plus">
        <TextButton
          variant="default"
          kind="secondary"
          onClick={onHandleHiddenOffer}
        >
          {intl.formatMessage({
            id: 'cF0wqDB2oT',
            defaultMessage: 'Unlock this offer via Free Preview',
            description: 'CTA button text to unlock a hidden offer by starting LuxPlus+ free preview',
          })}
        </TextButton>
      </ProductPaletteProvider>
    </div>
  </Group>
}

export default LuxPlusHiddenOfferDetails
